import { BILL_OF_LADING_TABLE_PARAMETER } from 'constants/configTableConstants';
import { cloneObj } from 'helpers/AppHelpers';
import { handleDateFilter, handleIdsFilter } from 'helpers/ChipGenerator';
import { useSearchParams } from 'hooks/useSearchParams';

const CREATED_PREFIX = 'Created';

const defaultFilter = {
  filters: {
    searchQuery: '',
    picklistIds: [],
    leavelistIds: [],
    originLocationIds: [],
    destinationLocationIds: [],
    createdFrom: null,
    createdTo: null
  },
  pagination: {
    page: 1,
    totalPages: 1,
    limit: 10
  },
  sortRules: [],
  responseReceived: false
};

const useSummaryParams = () => useSearchParams(['searchQuery']);

const generateInitialQuery = ({ getTableLimit, isAllLimitEnabled, filter, searchQuery }) => {
  const defaultPage = defaultFilter.pagination.page;
  const limit = getTableLimit(BILL_OF_LADING_TABLE_PARAMETER);

  const initialQuery = {
    filters: { ...defaultFilter.filters },
    pagination: { limit, page: defaultPage },
    sortRules: filter?.sortRules || defaultFilter.sortRules,
    responseReceived: false
  };

  const setFilters = (customFilters) => ({
    ...initialQuery,
    filters: { ...initialQuery.filters, ...customFilters }
  });

  if (searchQuery) {
    return setFilters({ searchQuery });
  }

  const isAll = isAllLimitEnabled(BILL_OF_LADING_TABLE_PARAMETER);
  const page = limit === 100 && isAll ? defaultPage : filter?.pagination?.page || defaultPage;

  return {
    ...initialQuery,
    filters: { ...initialQuery.filters, ...filter?.filters },
    pagination: { limit, page }
  };
};

const generateChips = (criteria, filters = {}) => {
  const { destinationLocations, leavelists, originLocations, picklists } = criteria;

  const { createdFrom, createdTo } = filters;

  let newChips = [];

  const filterConfigs = [
    { options: picklists, getLabel: (el) => el.name, key: 'picklistIds' },
    { options: leavelists, getLabel: (el) => el.name, key: 'leavelistIds' },
    { options: originLocations, getLabel: (el) => el.siteCode, key: 'originLocationIds' },
    { options: destinationLocations, getLabel: (el) => el.siteCode, key: 'destinationLocationIds' }
  ];

  filterConfigs.forEach(({ options, getLabel, key }) => {
    if (filters?.[key]?.length) {
      newChips = newChips.concat(handleIdsFilter(filters[key], options, getLabel, key));
    }
  });

  if (createdFrom || createdTo) {
    newChips = newChips.concat(handleDateFilter(createdFrom, createdTo, CREATED_PREFIX));
  }

  return newChips.filter(Boolean);
};

const removeChip = (filters, deletedChip) => {
  const newFilters = cloneObj(filters);
  const { key, itemId } = deletedChip;

  switch (key) {
    case `duration_${CREATED_PREFIX}`:
      newFilters.createdTo = null;
      newFilters.createdFrom = null;
      break;
    case `dateFrom_${CREATED_PREFIX}`:
      newFilters.createdFrom = null;
      break;
    case `dateTo_${CREATED_PREFIX}`:
      newFilters.createdTo = null;
      break;
    default:
      newFilters[key] = newFilters[key].filter((id) => id !== itemId);
      break;
  }

  return newFilters;
};

const parseQuery = (query) => {
  const payload = {
    filters: { searchQuery: '' },
    pagination: { page: query.pagination.page, limit: query.pagination.limit }
  };

  const setFilters = (key, value) => {
    if (value?.length || value) {
      payload.filters[key] = value;
    }
  };

  const filterKeys = Object.keys(defaultFilter.filters);

  filterKeys.forEach((key) => setFilters(key, query.filters[key]));

  if (query.sortRules) {
    payload.sortRules = query.sortRules;
  }

  return payload;
};

const tableConfig = [
  { id: 1, label: 'BOL ID', isSortable: true, parameter: 'id' },
  { id: 2, label: 'Freight Bill', isSortable: true, parameter: 'freightBill' },
  { id: 3, label: 'Picklist/Leavelist Name', isSortable: true, parameter: 'picklist.name' },
  { id: 4, label: 'Origin Location', isSortable: true, parameter: 'originLocation' },
  { id: 5, label: 'Destination', isSortable: true, parameter: 'destinationLocation' },
  { id: 6, label: 'Job Number', isSortable: true, parameter: 'locationJobNumber' },
  { id: 7, label: 'Created by', isSortable: true, parameter: 'createdByUser.email' },
  { id: 8, label: 'Created date', isSortable: true, parameter: 'createdAtUtc' }
];

export {
  defaultFilter,
  useSummaryParams,
  generateInitialQuery,
  generateChips,
  removeChip,
  parseQuery,
  tableConfig
};
